import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Activity,
  ArrowLeft,
  FolderOpen,
  Grid2x2Plus,
  Link2,
  LogOut,
  Pencil,
  Plus,
  Search,
  Settings,
  TableProperties,
  Trash2,
  User,
  X
} from 'lucide-react';
import toast from 'react-hot-toast';
import sheetLogo from '../../assets/images/sheetlogo.png';
import { auth } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import Profile from '../Profile/Profile';
import WorkspaceAiChat from './WorkspaceAiChat';
import WorkspaceDashboard from './WorkspaceDashboard';
import { sheetFormService } from '../../services/sheetFormService';
import { sheetService } from '../../services/sheetService';
import { sheetWorkspaceService } from '../../services/sheetWorkspaceService';
import { userService } from '../../services/userService';
import {
  getSheetPath,
  getSheetsLibraryUrl,
  getWorkspaceNewSheetPath,
  getWorkspaceEditPath,
  getWorkspacePath,
  getWorkspaceUrl,
  isLikelyWorkspaceCustomHost,
  normalizeWorkspaceCustomDomain
} from '../../utils/sheetDomains';
import {
  DEFAULT_WORKSPACE_PRIMARY_COLOR,
  normalizeWorkspacePrimaryColor,
  workspacePrimaryColorWithAlpha
} from '../../utils/sheetWorkspaceTheme';

const parseEmailList = (raw = '') => raw
  .split(',')
  .map((item) => item.trim().toLowerCase())
  .filter(Boolean)
  .filter((item, index, all) => all.indexOf(item) === index)
  .filter((email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));

const waitForAuthenticatedUser = async (expectedUid, attempts = 20, delayMs = 150) => {
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    const currentUser = auth.currentUser;
    if (currentUser?.uid && (!expectedUid || currentUser.uid === expectedUid)) {
      await currentUser.getIdToken();
      return currentUser;
    }
    await new Promise((resolve) => setTimeout(resolve, delayMs));
  }
  throw new Error('Authentication is not ready yet. Please try again.');
};

const formatDate = (timestamp) => {
  if (!timestamp) return 'Unknown';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const getWorkspaceSheetAccessLabel = (access) => {
  if (access === 'edit_own_rows') return 'Own rows';
  if (access === 'view_all_edit_own') return 'View all, edit own';
  if (access === 'edit') return 'Edit sheet';
  return 'View only';
};

const getSheetFormPath = (sheetId) => `/sheet-form/${sheetId}`;
const getSheetFormUrl = (sheetId) => `${window.location.origin}${getSheetFormPath(sheetId)}`;

const WorkspacePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { workspaceSlug } = useParams();
  const { user, userProfile, signOut } = useAuth();
  const userMenuRef = useRef(null);
  const autoOpenedWorkspaceEditorRef = useRef('');
  const [workspace, setWorkspace] = useState(null);
  const [sheets, setSheets] = useState([]);
  const [accessibleSheets, setAccessibleSheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [workspaceSaving, setWorkspaceSaving] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isWorkspaceModalOpen, setIsWorkspaceModalOpen] = useState(false);
  const [resolvedSharedUsers, setResolvedSharedUsers] = useState([]);
  const [selectedSharedUserIds, setSelectedSharedUserIds] = useState([]);
  const [allowedEmailsInput, setAllowedEmailsInput] = useState('');
  const [activeWorkspaceTab, setActiveWorkspaceTab] = useState('dashboard');
  const [workspaceAiOpen, setWorkspaceAiOpen] = useState(true);
  const [sheetFormsById, setSheetFormsById] = useState({});
  const [isAddSheetModalOpen, setIsAddSheetModalOpen] = useState(false);
  const [addSheetSearch, setAddSheetSearch] = useState('');
  const [selectedSheetIds, setSelectedSheetIds] = useState([]);
  const [addingSheets, setAddingSheets] = useState(false);
  const [workspaceForm, setWorkspaceForm] = useState({
    title: '',
    slug: '',
    customDomain: '',
    description: '',
    visibility: 'private',
    adminBuilderAccessEnabled: false,
    isTemplate: false
  });
  const [workspaceDomainEdited, setWorkspaceDomainEdited] = useState(false);
  const [workspaceCloning, setWorkspaceCloning] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    let isActive = true;

    const loadAdminState = async () => {
      if (!user?.uid) {
        if (isActive) setIsAdmin(false);
        return;
      }

      try {
        const openUser = await userService.getUserProfile(user.uid).catch(() => null);
        if (isActive) {
          setIsAdmin(`${openUser?.role || ''}`.trim() === 'admin');
        }
      } catch {
        if (isActive) setIsAdmin(false);
      }
    };

    loadAdminState();

    return () => {
      isActive = false;
    };
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid || (!workspaceSlug && !isLikelyWorkspaceCustomHost())) return;

    let isActive = true;

    const loadWorkspacePage = async () => {
      setLoading(true);
      setError('');
      try {
        await waitForAuthenticatedUser(user.uid);

        const workspaceData = workspaceSlug
          ? await sheetWorkspaceService.getWorkspaceBySlug(workspaceSlug)
          : await sheetWorkspaceService.getWorkspaceByCustomDomain(window.location.hostname);

        if (!isActive) return;

        setWorkspace(workspaceData);
        setSheets([]);
        setAccessibleSheets([]);

        if (`${workspaceData.userId || ''}`.trim() === `${user.uid || ''}`.trim()) {
          try {
            await sheetWorkspaceService.ensureWorkspaceDomainIndex(workspaceData.id);
          } catch (domainIndexError) {
            console.warn('Workspace domain index could not be ensured:', domainIndexError);
          }
        }

        const [ownedSheets, sharedSheets, adminWorkspaceSheets] = await Promise.all([
          sheetService.getUserSheets(user.uid).catch(() => []),
          sheetService.getSharedSheets(user.uid).catch(() => []),
          isAdmin && workspaceData.adminBuilderAccessEnabled === true
            ? sheetService.getSheetsByWorkspaceIds([workspaceData.id]).catch(() => [])
            : Promise.resolve([])
        ]);
        const allAccessibleSheets = [...ownedSheets, ...sharedSheets, ...adminWorkspaceSheets]
          .reduce((acc, sheet) => {
            if (!sheet?.id || acc.some((item) => item.id === sheet.id)) return acc;
            acc.push(sheet);
            return acc;
          }, []);
        const accessibleWorkspaceSheets = allAccessibleSheets
          .filter((sheet) => `${sheet.workspaceId || ''}`.trim() === workspaceData.id);
        if (!isActive) return;

        setSheets(accessibleWorkspaceSheets);
        setAccessibleSheets(allAccessibleSheets);
      } catch (err) {
        if (!isActive) return;
        setError(err?.message || 'Failed to load workspace.');
        setWorkspace(null);
        setSheets([]);
        setAccessibleSheets([]);
      } finally {
        if (isActive) setLoading(false);
      }
    };

    loadWorkspacePage();

    return () => {
      isActive = false;
    };
  }, [isAdmin, user?.uid, workspaceSlug]);

  useEffect(() => {
    const targetSheetIds = (Array.isArray(sheets) ? sheets : [])
      .map((sheet) => `${sheet?.id || ''}`.trim())
      .filter(Boolean);

    if (!targetSheetIds.length) {
      setSheetFormsById({});
      return;
    }

    let isActive = true;

    const loadSheetForms = async () => {
      try {
        const summaries = await sheetFormService.getSheetFormSummaries(targetSheetIds);
        if (!isActive) return;
        setSheetFormsById(summaries);
      } catch (err) {
        if (!isActive) return;
        console.warn('Workspace sheet form summaries could not be loaded:', err);
        setSheetFormsById({});
      }
    };

    loadSheetForms();

    return () => {
      isActive = false;
    };
  }, [sheets]);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (!userMenuRef.current?.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, []);

  const activeSheet = useMemo(
    () => sheets.find((sheet) => sheet.id === activeWorkspaceTab) || null,
    [activeWorkspaceTab, sheets]
  );
  const addableSheets = useMemo(() => {
    const normalizedSearch = addSheetSearch.trim().toLowerCase();
    return accessibleSheets.filter((sheet) => {
      if (`${sheet?.workspaceId || ''}`.trim() === `${workspace?.id || ''}`.trim()) return false;
      if (!normalizedSearch) return true;
      return [sheet?.title, sheet?.description, sheet?.workspaceTitle]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(normalizedSearch);
    });
  }, [accessibleSheets, addSheetSearch, workspace?.id]);

  const canManageWorkspace = Boolean(
    user?.uid &&
    (
      workspace?.userId === user.uid ||
      (isAdmin && workspace?.adminBuilderAccessEnabled === true)
    )
  );
  const workspaceLogo = `${workspace?.logoUrl || ''}`.trim() || sheetLogo;
  const workspacePrimaryColor = normalizeWorkspacePrimaryColor(workspace?.primaryColor)
    || DEFAULT_WORKSPACE_PRIMARY_COLOR;
  const workspaceThemeStyle = {
    '--workspace-primary-color': workspacePrimaryColor,
    '--workspace-primary-faint': workspacePrimaryColorWithAlpha(workspacePrimaryColor, 0.05),
    '--workspace-primary-soft': workspacePrimaryColorWithAlpha(workspacePrimaryColor, 0.1),
    '--workspace-primary-border': workspacePrimaryColorWithAlpha(workspacePrimaryColor, 0.2)
  };
  const activeSheetPrimaryColor = normalizeWorkspacePrimaryColor(activeSheet?.primaryColor)
    || workspacePrimaryColor;
  const activeSheetThemeStyle = {
    '--workspace-primary-color': activeSheetPrimaryColor,
    '--workspace-primary-faint': workspacePrimaryColorWithAlpha(activeSheetPrimaryColor, 0.05),
    '--workspace-primary-soft': workspacePrimaryColorWithAlpha(activeSheetPrimaryColor, 0.1),
    '--workspace-primary-border': workspacePrimaryColorWithAlpha(activeSheetPrimaryColor, 0.2)
  };

  useEffect(() => {
    if (activeWorkspaceTab === 'dashboard') return;
    if (!sheets.some((sheet) => sheet.id === activeWorkspaceTab)) {
      setActiveWorkspaceTab('dashboard');
    }
  }, [activeWorkspaceTab, sheets]);

  const getUserDisplayName = () => userProfile?.displayName || user?.displayName || user?.email?.split('@')[0] || 'User';

  const getUserInitials = () => {
    const source = `${userProfile?.displayName || user?.displayName || user?.email || ''}`.trim();
    if (!source) return 'U';
    return source
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() || '')
      .join('');
  };

  const openProfileModal = () => {
    setIsUserMenuOpen(false);
    setIsProfileModalOpen(true);
  };

  const openWorkspaceModal = useCallback(() => {
    if (!workspace) return;
    const nextVisibility = workspace.visibility === 'restricted' ? 'restricted' : 'private';
    setWorkspaceForm({
      title: `${workspace.title || ''}`,
      slug: `${workspace.slug || ''}`,
      customDomain: `${workspace.customDomain || ''}`,
      description: `${workspace.description || ''}`,
      visibility: nextVisibility,
      adminBuilderAccessEnabled: workspace.adminBuilderAccessEnabled === true,
      isTemplate: workspace.isTemplate === true
    });
    setWorkspaceDomainEdited(false);
    setIsWorkspaceModalOpen(true);
  }, [workspace]);

  useEffect(() => {
    const shouldOpenWorkspaceEditor = Boolean(location.state?.openWorkspaceEditor);
    const workspaceId = `${workspace?.id || ''}`.trim();
    if (!shouldOpenWorkspaceEditor || !workspaceId || !canManageWorkspace || isWorkspaceModalOpen) return;
    if (autoOpenedWorkspaceEditorRef.current === workspaceId) return;
    autoOpenedWorkspaceEditorRef.current = workspaceId;
    openWorkspaceModal();
  }, [canManageWorkspace, isWorkspaceModalOpen, location.state, openWorkspaceModal, workspace]);

  const handleSignOut = async () => {
    try {
      await signOut();
      setIsUserMenuOpen(false);
    } catch (signOutError) {
      console.error('Sign out error:', signOutError);
      toast.error(signOutError?.message || 'Failed to sign out.');
    }
  };

  const handleRemoveFromWorkspace = async (sheet) => {
    const confirmed = window.confirm(`Remove "${sheet.title}" from ${workspace?.title || 'this workspace'}?`);
    if (!confirmed) return;

    try {
      await sheetService.updateSheet(sheet.id, {
        workspaceId: null,
        workspaceTitle: null,
        workspaceOnly: false
      });
      setSheets((prev) => prev.filter((item) => item.id !== sheet.id));
      toast.success('Sheet removed from workspace.');
    } catch (err) {
      toast.error(err?.message || 'Failed to remove sheet from workspace.');
    }
  };

  const openAddSheetModal = () => {
    setAddSheetSearch('');
    setSelectedSheetIds([]);
    setIsAddSheetModalOpen(true);
  };

  const closeAddSheetModal = () => {
    if (addingSheets) return;
    setIsAddSheetModalOpen(false);
    setAddSheetSearch('');
    setSelectedSheetIds([]);
  };

  const handleToggleSheetSelection = (sheetId) => {
    setSelectedSheetIds((prev) => (
      prev.includes(sheetId)
        ? prev.filter((id) => id !== sheetId)
        : [...prev, sheetId]
    ));
  };

  const handleAddSheetsToWorkspace = async () => {
    if (!workspace?.id || !canManageWorkspace || !selectedSheetIds.length) return;

    const selectedSheets = accessibleSheets.filter((sheet) => selectedSheetIds.includes(sheet.id));
    if (!selectedSheets.length) return;

    try {
      setAddingSheets(true);
      const results = await Promise.allSettled(selectedSheets.map(async (sourceSheet) => {
        const isOwned = `${sourceSheet.userId || ''}`.trim() === `${user?.uid || ''}`.trim();
        const shouldClone = sourceSheet.visibility === 'public' || !isOwned;

        if (shouldClone) {
          const clonedSheet = await sheetService.cloneSheet(sourceSheet.id, user?.uid, {
            title: sourceSheet.title,
            workspaceId: workspace.id,
            workspaceTitle: workspace.title || '',
            workspaceOnly: true,
            includeRows: true,
            allowAccessibleSource: !isOwned
          });
          return { sheet: clonedSheet, mode: 'cloned' };
        }

        const updates = {
          workspaceId: workspace.id,
          workspaceTitle: workspace.title || '',
          workspaceOnly: true
        };
        const savedUpdates = await sheetService.updateSheet(sourceSheet.id, updates);
        return {
          sheet: { ...sourceSheet, ...updates, ...savedUpdates },
          mode: 'assigned'
        };
      }));
      const addedResults = results
        .filter((result) => result.status === 'fulfilled')
        .map((result) => result.value);
      const failedResults = results.filter((result) => result.status === 'rejected');

      if (!addedResults.length) {
        throw failedResults[0]?.reason || new Error('No sheets could be added.');
      }

      const addedSheets = addedResults.map((result) => result.sheet);
      const assignedSheetIds = new Set(
        addedResults.filter((result) => result.mode === 'assigned').map((result) => result.sheet.id)
      );

      setSheets((prev) => [...prev, ...addedSheets]);
      setAccessibleSheets((prev) => [
        ...prev.map((sheet) => (
          assignedSheetIds.has(sheet.id)
            ? addedSheets.find((addedSheet) => addedSheet.id === sheet.id) || sheet
            : sheet
        )),
        ...addedResults.filter((result) => result.mode === 'cloned').map((result) => result.sheet)
      ]);
      setActiveWorkspaceTab(addedSheets[0]?.id || 'dashboard');
      setIsAddSheetModalOpen(false);
      setAddSheetSearch('');
      setSelectedSheetIds([]);
      if (failedResults.length) {
        toast.error(`Added ${addedSheets.length} sheet${addedSheets.length === 1 ? '' : 's'}, but ${failedResults.length} could not be added.`);
      } else {
        toast.success(
          addedSheets.length === 1
            ? `Added ${addedSheets[0].title || 'sheet'} to the workspace.`
            : `Added ${addedSheets.length} sheets to the workspace.`
        );
      }
    } catch (err) {
      toast.error(err?.message || 'Failed to add sheets to the workspace.');
    } finally {
      setAddingSheets(false);
    }
  };

  const handleCloneWorkspace = async () => {
    if (!workspace?.id) return;

    const suggestedName = `${workspace.title || 'Workspace'} Copy`.trim();
    const requestedName = window.prompt('Name the cloned workspace:', suggestedName);
    if (requestedName === null) return;

    const cleanName = requestedName.trim();
    if (!cleanName) {
      toast.error('A workspace name is required.');
      return;
    }

    try {
      setWorkspaceCloning(true);
      const cloned = await sheetService.cloneWorkspace(workspace.id, user?.uid, {
        title: cleanName,
        includeRows: true
      });
      const nextWorkspace = cloned?.workspace;
      if (!nextWorkspace?.slug) {
        throw new Error('Workspace clone completed, but the new workspace route could not be resolved.');
      }
      navigate(getWorkspacePath(nextWorkspace.slug));
      toast.success('Workspace cloned.');
    } catch (err) {
      toast.error(err?.message || 'Failed to clone workspace.');
    } finally {
      setWorkspaceCloning(false);
    }
  };


  const handleOpenSheetForm = async (sheet) => {
    const cachedForm = sheetFormsById?.[sheet?.id];
    if (cachedForm) {
      window.open(getSheetFormUrl(sheet.id), '_blank', 'noopener,noreferrer');
      return;
    }

    try {
      const form = await sheetFormService.getSheetFormById(sheet.id);
      if (!form) {
        toast.error('This sheet does not have a published form yet.');
        return;
      }
      setSheetFormsById((prev) => ({ ...prev, [sheet.id]: form }));
      window.open(getSheetFormUrl(sheet.id), '_blank', 'noopener,noreferrer');
    } catch (err) {
      toast.error(err?.message || 'Unable to open the sheet form.');
    }
  };

  const handleToggleSharedUser = (userId) => {
    setSelectedSharedUserIds((prev) => (
      prev.includes(userId)
        ? prev.filter((item) => item !== userId)
        : [...prev, userId]
    ));
  };

  useEffect(() => {
    if (!isWorkspaceModalOpen || !workspace?.id || !canManageWorkspace) return;

    let isActive = true;

    const loadWorkspaceMembers = async () => {
      try {
        const members = await sheetWorkspaceService.getWorkspaceMembers(workspace.id);
        if (!isActive) return;

        const nonOwnerMembers = members.filter((member) => member.role !== 'owner');
        const memberEmails = nonOwnerMembers
          .map((member) => `${member.email || ''}`.trim().toLowerCase())
          .filter(Boolean);
        const memberUsers = nonOwnerMembers
          .filter((member) => member.userId && member.role !== 'owner')
          .map((member) => ({
            uid: member.userId,
            email: `${member.email || ''}`.trim().toLowerCase(),
            displayName: `${member.displayName || ''}`.trim()
          }));

        setAllowedEmailsInput(memberEmails.join(', '));
        setResolvedSharedUsers(memberUsers);
        setSelectedSharedUserIds(memberUsers.map((member) => member.uid));
      } catch (memberError) {
        if (!isActive) return;
        setAllowedEmailsInput('');
        setResolvedSharedUsers([]);
        setSelectedSharedUserIds([]);
      }
    };

    loadWorkspaceMembers();

    return () => {
      isActive = false;
    };
  }, [isWorkspaceModalOpen, workspace?.id, canManageWorkspace]);

  useEffect(() => {
    if (!isWorkspaceModalOpen || workspaceForm.visibility !== 'restricted' || !user?.uid) {
      if (workspaceForm.visibility !== 'restricted') {
        setResolvedSharedUsers([]);
        setSelectedSharedUserIds([]);
      }
      return;
    }

    const emails = parseEmailList(allowedEmailsInput);
    if (!emails.length) {
      setResolvedSharedUsers([]);
      setSelectedSharedUserIds([]);
      return;
    }

    let isActive = true;

    const loadResolvedSharedUsers = async () => {
      try {
        const users = await userService.findUsersByEmails(emails);
        if (!isActive) return;

        const matchedUsers = users
          .map((item) => ({
            uid: item.id || item.uid,
            email: `${item.email || ''}`.trim().toLowerCase(),
            displayName: `${item.displayName || ''}`.trim()
          }))
          .filter((item) => item.uid && emails.includes(item.email));

        setResolvedSharedUsers(matchedUsers);
        setSelectedSharedUserIds((prev) => {
          const existing = Array.isArray(prev) ? prev : [];
          const stillMatched = existing.filter((uid) => matchedUsers.some((matchedUser) => matchedUser.uid === uid));
          const newMatches = matchedUsers.map((matchedUser) => matchedUser.uid).filter((uid) => !stillMatched.includes(uid));
          return [...stillMatched, ...newMatches];
        });
      } catch (resolveError) {
        if (!isActive) return;
        setResolvedSharedUsers([]);
      }
    };

    loadResolvedSharedUsers();

    return () => {
      isActive = false;
    };
  }, [allowedEmailsInput, isWorkspaceModalOpen, user?.uid, workspaceForm.visibility]);

  const handleWorkspaceUpdate = async () => {
    if (!workspace?.id || !canManageWorkspace) return;

    const title = `${workspaceForm.title || ''}`.trim();
    const slug = `${workspaceForm.slug || ''}`.trim();
    const customDomain = normalizeWorkspaceCustomDomain(workspaceForm.customDomain);
    const previousSlug = `${workspace.slug || ''}`.trim();
    const previousCustomDomain = normalizeWorkspaceCustomDomain(workspace.customDomain);
    if (!title) {
      toast.error('Workspace name is required.');
      return;
    }
    if (!slug) {
      toast.error('Workspace URL path is required.');
      return;
    }

    try {
      setWorkspaceSaving(true);
      const basicUpdates = {
        title,
        description: `${workspaceForm.description || ''}`.trim(),
        visibility: workspaceForm.visibility,
        adminBuilderAccessEnabled: workspaceForm.adminBuilderAccessEnabled === true,
        isTemplate: workspaceForm.isTemplate === true
      };
      const routingUpdates = {};
      if (slug !== previousSlug) {
        routingUpdates.slug = slug;
      }

      await sheetWorkspaceService.updateWorkspaceBasics(workspace.id, basicUpdates);
      if (Object.keys(routingUpdates).length) {
        await sheetWorkspaceService.updateWorkspaceRouting(workspace.id, routingUpdates);
      }
      if (workspaceDomainEdited && customDomain !== previousCustomDomain) {
        await sheetWorkspaceService.updateWorkspaceCustomDomain(workspace.id, customDomain || null);
      }

      let memberSyncFailed = false;
      let memberSyncMessage = '';
      try {
        const currentMembers = await sheetWorkspaceService.getWorkspaceMembers(workspace.id);
        const existingNonOwnerMembers = currentMembers.filter((member) => member.role !== 'owner');
        const desiredUserIds = workspaceForm.visibility === 'restricted' ? selectedSharedUserIds : [];
        const desiredUsers = resolvedSharedUsers.filter((sharedUser) => desiredUserIds.includes(sharedUser.uid));

        await Promise.all(existingNonOwnerMembers.map((member) => sheetWorkspaceService.removeMember(workspace.id, member.userId)));
        await Promise.all(desiredUsers.map((sharedUser) => sheetWorkspaceService.addMember(workspace.id, {
          userId: sharedUser.uid,
          email: sharedUser.email,
          displayName: sharedUser.displayName,
          role: 'viewer',
          access: 'view'
        }, user?.uid)));
      } catch (memberError) {
        memberSyncFailed = true;
        console.warn('Workspace members could not be synchronized:', memberError);
        memberSyncMessage = memberError?.message || 'Permission denied while synchronizing workspace members [trace: member-sync].';
      }

      const nextWorkspace = {
        ...workspace,
        title,
        slug,
        customDomain: workspaceDomainEdited ? (customDomain || '') : workspace.customDomain,
        description: `${workspaceForm.description || ''}`.trim(),
        visibility: workspaceForm.visibility,
        adminBuilderAccessEnabled: workspaceForm.adminBuilderAccessEnabled === true,
        isTemplate: workspaceForm.isTemplate === true
      };
      setWorkspace(nextWorkspace);
      setIsWorkspaceModalOpen(false);
      if (memberSyncFailed) {
        toast.error(memberSyncMessage || 'Workspace updated, but member sharing could not be saved [trace: member-sync].');
      } else {
        toast.success('Workspace updated.');
      }
      if (workspaceDomainEdited && customDomain && customDomain !== previousCustomDomain) {
        window.location.assign(getWorkspaceUrl(nextWorkspace));
        return;
      }
      if (Object.prototype.hasOwnProperty.call(routingUpdates, 'slug')) {
        navigate(getWorkspacePath(slug), { replace: true });
      }
    } catch (err) {
      toast.error(err?.message || 'Failed to update workspace.');
    } finally {
      setWorkspaceSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="rounded-[20px] border border-[#d7dfd5] bg-white px-8 py-14 text-center text-slate-500 shadow-[0_28px_70px_-42px_rgba(15,23,42,0.38)]">
        Loading workspace...
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-[20px] border border-rose-200 bg-rose-50 px-8 py-10 text-rose-700 shadow-sm">
        {error}
      </div>
    );
  }

  if (!workspace) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#f4f7fb]" style={workspaceThemeStyle}>
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-56 flex-col overflow-hidden border-r border-[var(--workspace-primary-border)] bg-white px-4 py-6 text-[var(--workspace-primary-color)] shadow-[18px_0_48px_-28px_var(--workspace-primary-border)] xl:flex">
        <div className="flex h-16 items-center justify-center rounded-full bg-white px-3 shadow-sm ring-1 ring-[var(--workspace-primary-border)]">
          <img
            src={workspaceLogo}
            alt={`${workspace.title} logo`}
            className="max-h-12 w-auto max-w-full object-contain"
          />
        </div>
        <p className="mt-8 px-3 text-[9px] font-bold uppercase tracking-[0.24em] text-[var(--workspace-primary-color)] opacity-70">Workspace</p>
        <nav className="workspace-sidebar-scrollbar -mr-4 mt-3 min-h-0 flex-1 space-y-1.5 overflow-y-auto pr-4" aria-label="Workspace navigation">
          <button
            type="button"
            onClick={() => setActiveWorkspaceTab('dashboard')}
            className={`flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-semibold transition ${
              activeWorkspaceTab === 'dashboard'
                ? 'bg-[var(--workspace-primary-soft)] text-[var(--workspace-primary-color)] shadow-sm'
                : 'text-[var(--workspace-primary-color)] opacity-80 hover:bg-[var(--workspace-primary-faint)] hover:opacity-100'
            }`}
          >
            <Activity className="h-5 w-5 shrink-0" />
            Dashboard
          </button>
          {sheets.map((sheet) => (
            <button
              key={sheet.id}
              type="button"
              onClick={() => setActiveWorkspaceTab(sheet.id)}
              className={`flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-semibold transition ${
                activeWorkspaceTab === sheet.id
                  ? 'bg-[var(--workspace-primary-soft)] text-[var(--workspace-primary-color)] shadow-sm'
                  : 'text-[var(--workspace-primary-color)] opacity-80 hover:bg-[var(--workspace-primary-faint)] hover:opacity-100'
              }`}
              title={sheet.title}
            >
              <TableProperties className="h-5 w-5 shrink-0" />
              <span className="truncate">{sheet.title}</span>
            </button>
          ))}
        </nav>
        {canManageWorkspace && (
          <button
            type="button"
            onClick={openAddSheetModal}
            className="mt-3 flex w-full items-center gap-3 rounded-xl border border-dashed border-[var(--workspace-primary-border)] px-3 py-3 text-left text-sm font-semibold text-[var(--workspace-primary-color)] transition hover:border-[var(--workspace-primary-color)] hover:bg-[var(--workspace-primary-faint)]"
          >
            <Plus className="h-5 w-5" />
            Add sheet
          </button>
        )}
      </aside>

      <div className="min-h-screen w-full space-y-4 px-1 pb-6 pt-3 sm:px-2 xl:ml-56 xl:w-[calc(100%-14rem)] xl:px-4">
        <header className="rounded-2xl border border-slate-200/80 shadow-[0_16px_40px_-30px_rgba(15,23,42,0.5)]">
          <div className="px-2 py-2 sm:px-2">
            <div className="flex items-center justify-between gap-3 sm:items-center sm:gap-6">
              <div className="flex min-w-0 flex-1 items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    if (isLikelyWorkspaceCustomHost()) {
                      window.location.assign(getSheetsLibraryUrl());
                      return;
                    }
                    navigate(location.state?.fromAdminAccess ? '/sheets/admin-access' : '/sheets');
                  }}
                  aria-label="Back to sheets"
                  className="inline-flex h-8 w-8 shrink-0 items-center justify-center text-slate-500 transition hover:bg-slate-50 hover:text-slate-900"
                >
                  <ArrowLeft className="h-6 w-6" />
                </button>

                <div className="flex min-w-0 items-center gap-3 sm:gap-4">
                  <div className="relative shrink-0 xl:hidden">
                    <div className="flex h-10 w-auto items-center justify-center overflow-hidden lg:h-12 lg:w-auto">
                      <img
                        src={workspaceLogo}
                        alt={`${workspace.title} logo`}
                        className="h-full w-full object-contain"
                      />
                    </div>
                  </div>

                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                      <span
                        className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-[var(--workspace-primary-border)] bg-[var(--workspace-primary-faint)] text-xs font-bold uppercase tracking-[0.18em] text-[var(--workspace-primary-color)] sm:h-auto sm:w-auto sm:gap-2 sm:px-3 sm:py-2"
                        aria-label={workspace.slug}
                        title={workspace.slug}
                      >
                        <Link2 className="h-3.5 w-3.5" />
                        <span className="hidden sm:inline">{workspace.slug}</span>
                      </span>
                      <span
                        className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-[var(--workspace-primary-color)] text-xs font-bold uppercase tracking-[0.22em] text-white sm:h-auto sm:w-auto sm:gap-2 sm:px-3 sm:py-2"
                        aria-label="Workspace"
                        title="Workspace"
                      >
                        <FolderOpen className="h-3.5 w-3.5" />
                        <span className="hidden sm:inline">Workspace</span>
                      </span>
                      {workspace.isTemplate === true && (
                        <span className="inline-flex h-9 items-center justify-center rounded-full bg-amber-100 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-amber-800">
                          Template
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex shrink-0 items-center justify-end gap-2" ref={userMenuRef}>
                {user ? (
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setIsUserMenuOpen((prev) => !prev)}
                      className="flex min-w-0 items-center gap-3 rounded-xl border border-transparent bg-white p-0.5 transition hover:border-slate-200 hover:bg-slate-50 sm:px-2 sm:py-1.5"
                    >
                      {userProfile?.photoURL || user?.photoURL ? (
                        <img
                          src={userProfile?.photoURL || user?.photoURL}
                          alt="Profile"
                          className="h-9 w-9 rounded-full object-cover sm:h-12 sm:w-12"
                        />
                      ) : (
                        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-r from-indigo-500 to-blue-400 sm:h-12 sm:w-12">
                          <span className="text-xs font-semibold text-white sm:text-sm">{getUserInitials()}</span>
                        </div>
                      )}
                      <div className="hidden min-w-0 text-left sm:block">
                        <p className="max-w-[140px] truncate text-sm font-semibold text-slate-900 sm:text-base">{getUserDisplayName()}</p>
                        <p className="max-w-[140px] truncate text-xs text-slate-500 sm:text-sm">{user?.email || 'Signed in'}</p>
                      </div>
                    </button>

                    {isUserMenuOpen && (
                      <div className="absolute right-0 z-40 mt-2 w-52 overflow-hidden rounded-2xl border border-slate-200 bg-white py-1 shadow-xl">
                        <div className="border-b border-slate-100 px-4 py-3">
                          <p className="truncate text-sm font-medium text-slate-900">{getUserDisplayName()}</p>
                          <p className="truncate text-xs text-slate-500">{user?.email}</p>
                        </div>

                        <button
                          type="button"
                          onClick={openProfileModal}
                          className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-slate-700 transition hover:bg-slate-50"
                        >
                          <User className="h-4 w-4" />
                          Profile
                        </button>

                        <button
                          type="button"
                          onClick={() => {
                            setIsUserMenuOpen(false);
                            navigate('/settings');
                          }}
                          className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-slate-700 transition hover:bg-slate-50"
                        >
                          <Settings className="h-4 w-4" />
                          Settings
                        </button>

                        <button
                          type="button"
                          onClick={handleSignOut}
                          className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-rose-600 transition hover:bg-rose-50"
                        >
                          <LogOut className="h-4 w-4" />
                          Sign out
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => navigate('/login')}
                    className="flex items-center gap-3 rounded-full border border-slate-200 bg-white p-0.5 transition hover:bg-slate-50 sm:rounded-[20px] sm:px-5 sm:py-2"
                  >
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-r from-slate-300 to-slate-400 sm:h-12 sm:w-12">
                      <User className="h-4 w-4 text-white sm:h-5 sm:w-5" />
                    </div>
                    <span className="hidden text-sm font-semibold text-slate-700 sm:inline">Sign In</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </header>

        <section className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-[0_16px_40px_-32px_rgba(15,23,42,0.45)] xl:hidden">
          <div className="px-4 py-3">
            <div className="min-w-0">
              
              <div className="flex flex-wrap items-center gap-3">
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">Workspace tabs</p>
                <div className="flex max-w-full gap-2 overflow-x-auto pb-1">
                  <button
                    type="button"
                    onClick={() => setActiveWorkspaceTab('dashboard')}
                    className={`inline-flex shrink-0 items-center gap-2 rounded-2xl border px-4 py-2 text-sm font-semibold transition ${
                      activeWorkspaceTab === 'dashboard'
                        ? 'border-[var(--workspace-primary-color)] bg-[var(--workspace-primary-color)] text-white shadow-sm'
                        : 'border-[#d5ddd2] bg-[var(--workspace-primary-soft)] text-slate-600 hover:bg-white'
                    }`}
                  >
                    <Activity className="h-4 w-4" />
                    Dashboard
                  </button>
                  {sheets.map((sheet) => (
                    <button
                      key={sheet.id}
                      type="button"
                      onClick={() => setActiveWorkspaceTab(sheet.id)}
                      className={`inline-flex shrink-0 items-center gap-2 rounded-2xl border px-4 py-2 text-sm font-semibold transition ${
                        activeWorkspaceTab === sheet.id
                          ? 'border-[var(--workspace-primary-color)] bg-[var(--workspace-primary-color)] text-white shadow-sm'
                          : 'border-[#d5ddd2] bg-[var(--workspace-primary-soft)] text-slate-600 hover:bg-white'
                      }`}
                    >
                      <TableProperties className="h-4 w-4" />
                      <span className="max-w-[180px] truncate">{sheet.title}</span>
                    </button>
                  ))}
                  {canManageWorkspace && (
                    <button
                      type="button"
                      onClick={openAddSheetModal}
                      className="inline-flex shrink-0 items-center gap-2 rounded-2xl border border-dashed border-[var(--workspace-primary-color)] bg-white px-4 py-2 text-sm font-semibold text-[var(--workspace-primary-color)] transition hover:bg-[var(--workspace-primary-faint)]"
                      aria-label="Add accessible sheets to workspace tabs"
                    >
                      <Plus className="h-4 w-4" />
                      Add Sheet
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {activeWorkspaceTab === 'dashboard' ? (
          <WorkspaceDashboard
            workspace={workspace}
            sheets={sheets}
            canManageWorkspace={canManageWorkspace}
            onEdit={() => navigate(getWorkspaceEditPath(workspace.slug))}
            primaryColor={workspacePrimaryColor}
          />
        ) : (
          <section className="space-y-5">
            {sheets.length === 0 ? (
              <div className="rounded-[20px] border border-dashed border-[#c7d4c7] bg-white px-8 py-16 text-center shadow-sm">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-3xl bg-[#e6efe7] text-[#173229]">
                  <TableProperties className="h-7 w-7" />
                </div>
                <h3 className="mt-6 text-2xl font-bold text-slate-900">No sheets in this workspace</h3>
                <p className="mx-auto mt-2 max-w-2xl text-sm leading-6 text-slate-500">
                  Add existing sheets from the library or create a new one directly inside this workspace.
                </p>
                <div className="mt-6 flex flex-wrap justify-center gap-3">
                  {canManageWorkspace && (
                    <button
                      onClick={() => navigate(getWorkspaceNewSheetPath(workspace.slug), {
                        state: { workspaceId: workspace.id }
                      })}
                      className="inline-flex items-center gap-2 rounded-2xl bg-[#173229] px-5 py-3 text-sm font-semibold text-[#f4f5e6] transition hover:bg-[#0f261e]"
                    >
                      <Plus className="h-4 w-4" />
                      Create Sheet
                    </button>
                  )}
                  <button
                    onClick={() => {
                      if (isLikelyWorkspaceCustomHost()) {
                        window.location.assign(getSheetsLibraryUrl());
                        return;
                      }
                      navigate(location.state?.fromAdminAccess ? '/sheets/admin-access' : '/sheets');
                    }}
                    className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                  >
                    <Grid2x2Plus className="h-4 w-4" />
                    Open Library
                  </button>
                </div>
              </div>
            ) : activeSheet ? (
              <div className="space-y-4" style={activeSheetThemeStyle}>
                <div className="flex flex-col gap-4 rounded-2xl border border-slate-200/80 bg-white p-5 shadow-[0_16px_40px_-32px_rgba(15,23,42,0.5)] lg:flex-row lg:items-center lg:justify-between">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--workspace-primary-color)]">Workspace / Sheet</p>
                      {activeSheet.userId !== user?.uid && (
                        <span className="rounded-full bg-sky-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-sky-700">
                          Shared
                        </span>
                      )}
                      <span className="rounded-full bg-[var(--workspace-primary-soft)] px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-[#0f4f4f]">
                        {activeSheet.visibility || 'private'}
                      </span>
                    </div>
                    <h2 className="mt-2 truncate text-2xl font-black tracking-tight text-slate-950">{activeSheet.title}</h2>
                    <p className="mt-1 max-w-3xl text-sm leading-6 text-slate-500">
                      {activeSheet.description || 'This sheet is rendering directly inside the workspace. Use full view if you need the standalone page.'}
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2 text-xs font-semibold text-slate-600">
                      <span className="rounded-lg bg-slate-100 px-2.5 py-1.5">{activeSheet.headers?.length || 0} columns</span>
                      <span className="rounded-lg bg-slate-100 px-2.5 py-1.5">{getWorkspaceSheetAccessLabel(activeSheet.collaboratorAccess)}</span>
                      <span className="rounded-lg bg-slate-100 px-2.5 py-1.5">Updated {formatDate(activeSheet.updatedAt || activeSheet.createdAt)}</span>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 lg:justify-end">
                    <button
                      onClick={() => navigate(getSheetPath(activeSheet.id))}
                      className="inline-flex items-center gap-2 rounded-xl bg-[var(--workspace-primary-color)] px-4 py-2.5 text-xs font-semibold text-white shadow-sm transition hover:opacity-90"
                    >
                      Open Full View
                    </button>
                    {(Boolean(sheetFormsById?.[activeSheet.id]) || activeSheet.userId === user?.uid) && (
                      <button
                        onClick={() => handleOpenSheetForm(activeSheet)}
                        className="inline-flex items-center gap-2 rounded-xl border border-[var(--workspace-primary-border)] bg-[var(--workspace-primary-faint)] px-3.5 py-2.5 text-xs font-semibold text-[var(--workspace-primary-color)] transition hover:bg-[var(--workspace-primary-soft)]"
                      >
                        Form
                      </button>
                    )}
                    {activeSheet.userId === user?.uid && (
                      <>
                        <button
                          onClick={() => navigate(`/sheets/${activeSheet.id}/edit`, {
                            state: {
                              returnTo: getWorkspacePath(workspace.slug)
                            }
                          })}
                          className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3.5 py-2.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
                        >
                          <Pencil className="h-4 w-4" />
                          Edit
                        </button>
                        <button
                          onClick={() => handleRemoveFromWorkspace(activeSheet)}
                          className="inline-flex items-center gap-2 rounded-xl border border-rose-200 px-3.5 py-2.5 text-xs font-semibold text-rose-600 transition hover:bg-rose-50"
                        >
                          <Trash2 className="h-4 w-4" />
                          Remove
                        </button>
                      </>
                    )}
                  </div>
                </div>

                <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-[0_20px_55px_-38px_rgba(15,23,42,0.5)]">
                  <iframe
                    title={`${activeSheet.title} workspace view`}
                    src={`${getSheetPath(activeSheet.id)}?embedded=1&themeColor=${encodeURIComponent(activeSheetPrimaryColor)}`}
                    className="h-[calc(100vh-15.5rem)] min-h-[680px] w-full border-0"
                  />
                </div>
              </div>
            ) : null}
          </section>
        )}
      </div>

      {user && (
        <WorkspaceAiChat
          workspace={workspace}
          sheets={sheets}
          isOpen={workspaceAiOpen}
          onOpenChange={setWorkspaceAiOpen}
        />
      )}

      {isProfileModalOpen && <Profile isModal onClose={() => setIsProfileModalOpen(false)} />}
      {isAddSheetModalOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/50 px-4 py-4 sm:py-6"
          onClick={closeAddSheetModal}
        >
          <div
            className="flex max-h-[calc(100vh-2rem)] w-full max-w-2xl flex-col overflow-hidden rounded-[20px] border border-[#d5ddd2] bg-white shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex shrink-0 items-start justify-between gap-4 border-b border-slate-200 px-6 py-5">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#5f7a67]">Workspace Tabs</p>
                <h2 className="mt-2 text-2xl font-black tracking-tight text-slate-900">Add existing sheets</h2>
                <p className="mt-2 text-sm text-slate-500">
                  Owned non-public sheets are moved here with their existing items. Public or shared sheets are copied with data.
                </p>
              </div>
              <button
                type="button"
                onClick={closeAddSheetModal}
                disabled={addingSheets}
                className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-200 text-slate-500 transition hover:bg-slate-50 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-60"
                aria-label="Close add sheets dialog"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-6">
              <label className="relative block">
                <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  type="search"
                  value={addSheetSearch}
                  onChange={(event) => setAddSheetSearch(event.target.value)}
                  placeholder="Search accessible sheets"
                  className="w-full rounded-2xl border border-slate-200 py-3 pl-11 pr-4 text-sm text-slate-900 outline-none transition focus:border-[var(--workspace-primary-color)]"
                />
              </label>

              <div className="mt-5 space-y-3">
                {addableSheets.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-5 py-10 text-center text-sm text-slate-500">
                    {addSheetSearch.trim()
                      ? 'No accessible sheets match your search.'
                      : 'All accessible sheets are already in this workspace, or no other sheets are available.'}
                  </div>
                ) : (
                  addableSheets.map((sheet) => {
                    const isOwned = `${sheet.userId || ''}`.trim() === `${user?.uid || ''}`.trim();
                    const willClone = sheet.visibility === 'public' || !isOwned;
                    const isSelected = selectedSheetIds.includes(sheet.id);
                    return (
                      <label
                        key={sheet.id}
                        className={`flex cursor-pointer items-start gap-4 rounded-2xl border px-4 py-4 transition ${
                          isSelected
                            ? 'border-[var(--workspace-primary-color)] bg-[var(--workspace-primary-faint)]'
                            : 'border-slate-200 bg-white hover:bg-slate-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggleSheetSelection(sheet.id)}
                          disabled={addingSheets}
                          className="mt-1 h-4 w-4 rounded border-slate-300 text-[var(--workspace-primary-color)] focus:ring-[var(--workspace-primary-color)]"
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="truncate text-sm font-semibold text-slate-900">{sheet.title || 'Untitled Sheet'}</p>
                            <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] ${
                              isOwned ? 'bg-emerald-50 text-emerald-700' : 'bg-sky-50 text-sky-700'
                            }`}>
                              {isOwned ? 'Owned' : 'Shared'}
                            </span>
                            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-600">
                              {willClone ? 'Copy with data' : 'Move existing'}
                            </span>
                          </div>
                          <p className="mt-1 line-clamp-2 text-sm text-slate-500">
                            {sheet.description || `${sheet.headers?.length || 0} columns`}
                          </p>
                          {sheet.workspaceTitle && (
                            <p className="mt-2 text-xs font-medium text-slate-400">Currently in {sheet.workspaceTitle}</p>
                          )}
                        </div>
                      </label>
                    );
                  })
                )}
              </div>
            </div>

            <div className="mt-auto flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-slate-200 px-6 py-5">
              <p className="text-sm text-slate-500">
                {selectedSheetIds.length} selected
              </p>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={closeAddSheetModal}
                  disabled={addingSheets}
                  className="inline-flex items-center rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleAddSheetsToWorkspace}
                  disabled={addingSheets || selectedSheetIds.length === 0}
                  className="inline-flex items-center rounded-2xl bg-[var(--workspace-primary-color)] px-5 py-3 text-sm font-semibold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {addingSheets
                    ? 'Adding...'
                    : selectedSheetIds.length
                      ? `Add ${selectedSheetIds.length} Sheet${selectedSheetIds.length === 1 ? '' : 's'}`
                      : 'Add Sheets'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {isWorkspaceModalOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/50 px-4 py-4 sm:py-6"
          onClick={() => !workspaceSaving && setIsWorkspaceModalOpen(false)}
        >
          <div
            className="flex max-h-[calc(100vh-2rem)] w-full max-w-2xl flex-col overflow-hidden rounded-[20px] border border-[#d5ddd2] bg-white shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex shrink-0 items-start justify-between gap-4 border-b border-slate-200 px-6 py-5">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#5f7a67]">Workspace Settings</p>
                <h2 className="mt-2 text-2xl font-black tracking-tight text-slate-900">Update Workspace</h2>
              </div>
              <button
                type="button"
                onClick={() => !workspaceSaving && setIsWorkspaceModalOpen(false)}
                className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 text-slate-500 transition hover:bg-slate-50 hover:text-slate-900"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="grid flex-1 gap-5 overflow-y-auto px-6 py-6">
              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-700">Workspace Name</label>
                <input
                  type="text"
                  value={workspaceForm.title}
                  onChange={(event) => setWorkspaceForm((prev) => ({ ...prev, title: event.target.value }))}
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[var(--workspace-primary-color)]"
                  placeholder="Operations Hub"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-700">Workspace URL Path</label>
                <input
                  type="text"
                  value={workspaceForm.slug}
                  onChange={(event) => setWorkspaceForm((prev) => ({ ...prev, slug: event.target.value }))}
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[var(--workspace-primary-color)]"
                  placeholder="operations-hub"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-700">Custom Domain</label>
                <input
                  type="text"
                  value={workspaceForm.customDomain}
                  onChange={(event) => {
                    setWorkspaceDomainEdited(true);
                    setWorkspaceForm((prev) => ({ ...prev, customDomain: event.target.value }));
                  }}
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[var(--workspace-primary-color)]"
                  placeholder="workspace.yourdomain.com"
                />
                <p className="mt-2 text-xs text-slate-500">
                  Point this domain to the sheets app and it will open this workspace directly.
                </p>
              </div>

              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-700">Description</label>
                <textarea
                  rows={4}
                  value={workspaceForm.description}
                  onChange={(event) => setWorkspaceForm((prev) => ({ ...prev, description: event.target.value }))}
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[var(--workspace-primary-color)]"
                  placeholder="Describe what this workspace is for."
                />
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-sm font-semibold text-slate-700">Admin builder access</p>
                    <p className="mt-1 text-xs text-slate-500">
                      Allow admins to find this workspace in the admin access section and open its management editor without granting row-level data access.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setWorkspaceForm((prev) => ({
                      ...prev,
                      adminBuilderAccessEnabled: !(prev.adminBuilderAccessEnabled === true)
                    }))}
                    className={`inline-flex h-7 w-12 items-center rounded-full border transition ${
                      workspaceForm.adminBuilderAccessEnabled
                        ? 'border-[var(--workspace-primary-color)] bg-[var(--workspace-primary-color)]'
                        : 'border-slate-300 bg-slate-200'
                    }`}
                    aria-pressed={workspaceForm.adminBuilderAccessEnabled === true}
                  >
                    <span
                      className={`inline-block h-5 w-5 rounded-full bg-white shadow transition ${
                        workspaceForm.adminBuilderAccessEnabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-700">Visibility</label>
                <div className="grid gap-3 sm:grid-cols-2">
                  {[
                    { value: 'private', label: 'Private' },
                    { value: 'restricted', label: 'Restricted' },
                    { value: 'template', label: 'Template' }
                  ].map((visibilityOption) => (
                    <button
                      key={visibilityOption.value}
                      type="button"
                      onClick={() => setWorkspaceForm((prev) => ({
                        ...prev,
                        visibility: visibilityOption.value === 'restricted' ? 'restricted' : 'private',
                        isTemplate: visibilityOption.value === 'template'
                      }))}
                      className={`rounded-2xl border px-4 py-3 text-left text-sm font-semibold transition ${
                        ((visibilityOption.value === 'template' && workspaceForm.isTemplate === true) ||
                        (visibilityOption.value !== 'template' && workspaceForm.isTemplate !== true && workspaceForm.visibility === visibilityOption.value))
                          ? 'border-[var(--workspace-primary-color)] bg-[#e8f6f6] text-[#0f4f4f]'
                          : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      {visibilityOption.label}
                    </button>
                  ))}
                </div>
              </div>

              {workspaceForm.isTemplate === true && (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                  Template mode makes this workspace discoverable in the templates directory for signed-in users to clone.
                </div>
              )}

              {workspaceForm.visibility === 'restricted' && workspaceForm.isTemplate !== true && (
                <div className="grid gap-5">
                  <div>
                    <label className="mb-2 block text-sm font-semibold text-slate-700">Allowed Emails</label>
                    <input
                      value={allowedEmailsInput}
                      onChange={(event) => setAllowedEmailsInput(event.target.value)}
                      placeholder="name@example.com, second@example.com"
                      className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[var(--workspace-primary-color)]"
                    />
                    {resolvedSharedUsers.length > 0 && (
                      <div className="mt-3 rounded-2xl border border-slate-200 bg-white p-3">
                        <p className="text-[11px] font-semibold tracking-wide text-slate-500">Matched Signed-In Users</p>
                        <div className="mt-2 space-y-2">
                          {resolvedSharedUsers.map((sharedUser) => (
                            <label
                              key={sharedUser.uid}
                              className="flex items-start gap-3 rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700"
                            >
                              <input
                                type="checkbox"
                                checked={selectedSharedUserIds.includes(sharedUser.uid)}
                                onChange={() => handleToggleSharedUser(sharedUser.uid)}
                                className="mt-1 h-4 w-4 rounded border-slate-300 text-[var(--workspace-primary-color)] focus:ring-[var(--workspace-primary-color)]"
                              />
                              <span>
                                <span className="block font-semibold text-slate-800">
                                  {sharedUser.displayName || sharedUser.email}
                                </span>
                                <span className="block text-xs text-slate-500">
                                  {sharedUser.email} | {sharedUser.uid}
                                </span>
                              </span>
                            </label>
                          ))}
                        </div>
                        <p className="mt-2 text-xs text-slate-500">
                          Checked users are added to the workspace as members. Unmatched emails will not be added until they map to an existing signed-in user.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">Clone Workspace</p>
                    <p className="mt-1 text-xs text-slate-500">
                      Duplicate this workspace, its sheets, and linked forms into a new workspace with a new name.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleCloneWorkspace}
                    disabled={workspaceSaving || workspaceCloning}
                    className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {workspaceCloning ? 'Cloning...' : 'Clone Workspace'}
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-auto flex shrink-0 flex-wrap justify-end gap-3 border-t border-slate-200 px-6 py-5">
              <button
                type="button"
                onClick={() => setIsWorkspaceModalOpen(false)}
                disabled={workspaceSaving}
                className="inline-flex items-center rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleWorkspaceUpdate}
                disabled={workspaceSaving}
                className="inline-flex items-center rounded-2xl bg-[var(--workspace-primary-color)] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#0f6d6d] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {workspaceSaving ? 'Saving...' : 'Save Workspace'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkspacePage;
